import { useMemo, useState } from 'react'
import type { UnifiedTimelineEntry } from '../lib/api'

type TimelineFilter = 'all' | 'customer' | 'operations' | 'appointments' | 'inspections'

function formatWhen(timestamp?: string) {
  if (!timestamp) return 'Unknown time'
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return 'Unknown time'
  return date.toLocaleString()
}

function matchesFilter(entry: UnifiedTimelineEntry, filter: TimelineFilter) {
  const type = String(entry.eventType || '').toLowerCase()
  if (filter === 'all') return true
  if (filter === 'customer') return (entry.visibility === 'customer') || type.startsWith('quote.') || type.startsWith('customer.')
  if (filter === 'operations') return type.startsWith('trip.') || type.startsWith('dispatch.') || type.startsWith('order.')
  if (filter === 'appointments') return type.includes('appointment')
  if (filter === 'inspections') return type.includes('inspection') || type.includes('condition.issue')
  return true
}

function filterLabel(filter: TimelineFilter) {
  const labels: Record<TimelineFilter, string> = {
    all: 'All',
    customer: 'Customer-visible',
    operations: 'Operations',
    appointments: 'Appointments',
    inspections: 'Inspections / Issues',
  }
  return labels[filter]
}

export default function UnifiedTimeline({
  title,
  entries,
  defaultFilter = 'all',
}: {
  title: string
  entries: UnifiedTimelineEntry[]
  defaultFilter?: TimelineFilter
}) {
  const [filter, setFilter] = useState<TimelineFilter>(defaultFilter)

  const filtered = useMemo(() => {
    return (entries || []).filter((entry) => matchesFilter(entry, filter))
  }, [entries, filter])

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{title}</h3>
        <div className="flex flex-wrap gap-2">
          {(['all', 'customer', 'operations', 'appointments', 'inspections'] as TimelineFilter[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFilter(item)}
              className={`text-xs px-2 py-1 rounded border ${filter === item ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {filterLabel(item)}
            </button>
          ))}
        </div>
      </div>

      {!filtered.length ? (
        <p className="text-sm text-gray-400">No timeline events yet.</p>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 80).map((entry) => (
            <div key={entry.id} className="border rounded-lg p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-900">{entry.label || entry.eventType}</div>
                <div className="text-xs text-gray-500">{formatWhen(entry.timestamp)}</div>
              </div>
              <div className="mt-1 text-xs text-gray-600">
                {entry.actorName ? `${entry.actorName}` : 'System'}
                {entry.actorRole ? ` (${entry.actorRole})` : ''}
              </div>
              {entry.metadataSummary && <div className="mt-1 text-xs text-gray-600">{entry.metadataSummary}</div>}
              {entry.relatedObjectLink && (
                <div className="mt-1">
                  <a href={entry.relatedObjectLink} className="text-xs text-blue-700 hover:underline">Open related record</a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
